import { useState, useEffect, useRef } from 'react'
import { useGetAdminReportsQuery, useResolveReportMutation } from '../../store/api/moderationApi'
import type { ContentReport } from '../../store/api/moderationApi'
import { Tabs, EmptyState, SentinelLoader } from '../../components/ui'
import { COLOR_WARNING, COLOR_SUCCESS, COLOR_ERROR, COLOR_AI_TAG_BG, COLOR_CYAN, COLOR_ERROR_BG, COLOR_ERROR_BORDER } from '../../lib/config'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

export function AdminReports() {
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const { data, isLoading, isFetching, refetch } = useGetAdminReportsQuery({ status: statusFilter, cursor, limit: 20 })
  const [resolveReport] = useResolveReportMutation()
  const [resolvingId, setResolvingId] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const reports = data?.data ?? []

  useEffect(() => { setCursor(undefined) }, [statusFilter])

  useEffect(() => {
    if (!sentinelRef.current || !data?.hasMore || isFetching) return
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && data?.hasMore && !isFetching) setCursor(data.nextCursor ?? undefined)
    }, { rootMargin: '0px 0px 120px 0px' })
    obs.observe(sentinelRef.current)
    return () => obs.disconnect()
  }, [data?.hasMore, data?.nextCursor, isFetching])

  const handleAction = async (id: string, action: 'resolve' | 'dismiss') => {
    setResolvingId(id)
    try { await resolveReport({ id, action }).unwrap() } catch {}
    setResolvingId(null)
  }

  const STATUS_TABS = ['pending', 'resolved', 'dismissed'] as const
  const statusColor: Record<string, string> = { pending: COLOR_WARNING, resolved: COLOR_SUCCESS, dismissed: 'var(--text-muted)' }

  return (
    <div>
      <Tabs
        tabs={STATUS_TABS.map(s => ({ key: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
        active={statusFilter}
        onChange={setStatusFilter}
        rightSlot={<AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />}
        style={{ marginBottom: '1.25rem' }}
      />

      {isLoading && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>}

      {!isLoading && reports.length === 0 && (
        <EmptyState icon="🚩" title={`No ${statusFilter} reports`} description="Content reports will appear here when users flag content" />
      )}

      {!isLoading && reports.length > 0 && (
        <div className={s.listCard} style={{ marginBottom: '0.5rem' }}>
          {reports.map((report: ContentReport) => (
            <div key={report.id} className={s.reportRow}>
              <div className={s.reportMeta}>
                <span className={s.reportTypeBadge} style={{ background: report.targetType === 'discussion' ? COLOR_AI_TAG_BG : 'rgba(6,182,212,0.12)', color: report.targetType === 'discussion' ? 'var(--accent-primary)' : COLOR_CYAN }}>{report.targetType}</span>
                <span className={s.reportStatusBadge} style={{ background: `${statusColor[report.status]}22`, color: statusColor[report.status] }}>{report.status}</span>
                <span className={s.reportBy}>reported by <strong style={{ color: 'var(--text-secondary)' }}>{report.reporter.profile?.displayName ?? 'User'}</strong></span>
                <span className={s.reportDate}>{new Date(report.createdAt).toLocaleDateString()}</span>
              </div>
              {report.contentTitle && (
                <div className={s.reportContent}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className={s.reportContentLabel}>{report.targetType === 'comment' ? 'In:' : 'Post:'}</span>
                    {report.discussionId ? (
                      <a href={`/discussions/${report.discussionId}`} target="_blank" rel="noreferrer" className={s.reportContentLink}>{report.contentTitle}</a>
                    ) : (
                      <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>{report.contentTitle}</span>
                    )}
                  </div>
                  {report.contentPreviewText && (
                    <p className={s.reportPreview} style={{ paddingLeft: report.targetType === 'comment' ? '2.5rem' : '2.75rem' }}>
                      {report.targetType === 'comment' && <span style={{ color: 'var(--text-muted)', marginRight: '0.3rem' }}>↩</span>}
                      {report.contentPreviewText}
                    </p>
                  )}
                </div>
              )}
              <div className={s.reportReason}><span className={s.reportReasonLabel}>Reason: </span>{report.reason}</div>
              {report.status === 'pending' && (
                <div className={s.reportActions}>
                  <button onClick={() => void handleAction(report.id, 'resolve')} disabled={resolvingId === report.id}
                    className={s.reportActionBtn}
                    style={{ background: COLOR_ERROR_BG, color: COLOR_ERROR, border: `1px solid ${COLOR_ERROR_BORDER}`, cursor: resolvingId === report.id ? 'not-allowed' : 'pointer', opacity: resolvingId === report.id ? 0.6 : 1 }}>
                    {resolvingId === report.id ? '...' : '✔ Resolve & remove content'}
                  </button>
                  <button onClick={() => void handleAction(report.id, 'dismiss')} disabled={resolvingId === report.id}
                    className={s.reportActionBtn}
                    style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: resolvingId === report.id ? 'not-allowed' : 'pointer', opacity: resolvingId === report.id ? 0.6 : 1 }}>
                    ✕ Dismiss
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <SentinelLoader ref={sentinelRef} isFetching={isFetching && reports.length > 0} />
    </div>
  )
}
